import { ExternalLink } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { TriggerBadge } from "@/components/ci/trigger-badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCiJob } from "@/hooks/use-ci";
import { cn } from "@/lib/utils";

const ROLLUP_TILES = [
  ["total", "cells", "text-foreground"],
  ["passed", "passed", "text-emerald-500"],
  ["failed", "failed", "text-rose-500"],
  ["skipped", "skipped", "text-muted-foreground"],
  ["xfailed", "xfail", "text-yellow-600"],
  ["flaky", "flaky", "text-muted-foreground"],
  ["regressions", "regressions", "text-rose-500"],
] as const;

const TERMINAL_JOB_STATES = new Set(["completed", "failed", "cancelled", "expired"]);
const STALE_SNAPSHOT_MS = 3 * 60_000;

// A finished job has no live board: the Runs/tests tables are the truth, and a
// leftover mid-batch snapshot ("running 1/4") reads as a lie.
function snapshotAge(receivedAt: string | undefined): string | null {
  if (!receivedAt) return null;
  const seconds = Math.max(0, Math.round((Date.now() - new Date(receivedAt).getTime()) / 1000));
  return seconds < 90 ? `${seconds}s ago` : `${Math.round(seconds / 60)}m ago`;
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return "-";
  // Same rule as the run page's table: Mooncake suites carry sub-second timings.
  if (seconds < 60) return `${seconds.toFixed(seconds < 1 ? 2 : 1)}s`;
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function CiJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { data: job, isLoading } = useCiJob(jobId);

  if (isLoading || !job)
    return (
      <div className="mx-auto max-w-screen-xl p-6">
        <Skeleton className="h-64 w-full" />
      </div>
    );

  const reporters = TERMINAL_JOB_STATES.has(job.state)
    ? []
    : Object.entries(job.progress?.reporters ?? {});
  const summary = job.result_summary ?? {};
  const error = typeof summary.error === "string" ? summary.error : "";
  const logTail = typeof summary.log_tail === "string" ? summary.log_tail : "";

  return (
    <div className="mx-auto max-w-screen-xl space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-xl font-semibold">{job.job_id}</h1>
        <CiStatusBadge status={job.state} />
        <TriggerBadge trigger={job.trigger} releaseTag={job.release_tag} />
        <span className="text-sm text-muted-foreground">
          {job.requested_by ? `by ${job.requested_by}` : ""}
        </span>
        {job.cryod_version && (
          <span className="rounded-full border bg-muted/40 px-2 py-0.5 font-mono text-xs text-muted-foreground">
            cryod {job.cryod_version}
          </span>
        )}
        {job.claimed_by?.startsWith("http") && (
          <a
            href={job.claimed_by}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
          >
            CI run <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {ROLLUP_TILES.map(([key, label, color]) => (
          <Card key={key}>
            <CardContent className="p-4">
              <div className={cn("text-2xl font-semibold tabular-nums", color)}>
                {job.rollup[key]}
              </div>
              <div className="text-xs text-muted-foreground">{label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(error || logTail) && (
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="text-sm font-medium">Why it failed</div>
            {error && <div className="text-sm text-rose-500">{error}</div>}
            {logTail && (
              <pre className="max-h-96 overflow-auto rounded-md bg-muted/40 p-3 font-mono text-xs">
                {logTail}
              </pre>
            )}
          </CardContent>
        </Card>
      )}

      {reporters.length > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">Clusters</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {reporters.map(([name, snap]) => {
              const done = snap.cells_done ?? 0;
              const total = snap.cells_total ?? 0;
              return (
                <Card key={name}>
                  <CardContent className="space-y-2 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-mono text-sm font-medium">
                        {snap.cluster?.machines?.length ? snap.cluster.machines.join(" · ") : name}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {snap.phase ?? "-"}
                        {(() => {
                          const age = snapshotAge(snap._received_at);
                          const stale =
                            snap._received_at &&
                            Date.now() - new Date(snap._received_at).getTime() > STALE_SNAPSHOT_MS;
                          return age ? (
                            <span className={stale ? "ml-1.5 text-rose-500" : "ml-1.5 opacity-60"}>
                              · {age}
                            </span>
                          ) : null;
                        })()}
                      </span>
                    </div>
                    {snap.cells_total != null && (
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: total ? `${(100 * done) / total}%` : "0%" }}
                        />
                      </div>
                    )}
                    {snap.activity && (
                      <div className="whitespace-pre-line break-all font-mono text-xs">
                        {snap.activity}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {snap.cells_total != null
                        ? `${done}/${total} cells`
                        : ((snap.reason as string) ?? "")}
                      {snap.error ? ` · ${snap.error}` : ""}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {(job.rollup.failures?.length ?? 0) > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">Failures</h2>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test</TableHead>
                  <TableHead>Classification</TableHead>
                  <TableHead>Verdict</TableHead>
                  <TableHead>Run</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {job.rollup.failures?.map((failure) => (
                  <TableRow
                    key={failure.result_id}
                    className="cursor-pointer border-l-2 border-l-rose-500"
                    onClick={() => navigate(`/ci/${failure.run_id}/tests/${failure.result_id}`)}
                  >
                    <TableCell className="font-mono text-sm">{failure.test_name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {failure.classification ?? "-"}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "text-xs font-medium",
                          failure.transition === "regression"
                            ? "text-rose-500"
                            : "text-muted-foreground",
                        )}
                      >
                        {failure.transition === "regression"
                          ? "NEW regression"
                          : (failure.transition?.replace("_", " ") ?? "unclassified")}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {failure.run_id}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-2 text-sm font-medium text-muted-foreground">Runs</h2>
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Tests</TableHead>
                <TableHead className="text-right">Passed</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Skipped</TableHead>
                <TableHead className="text-right">xfail</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.runs.map((run) => (
                <TableRow
                  key={run.run_id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/ci/${run.run_id}`)}
                >
                  <TableCell className="font-mono text-sm">{run.run_id}</TableCell>
                  <TableCell>
                    <CiStatusBadge status={run.status} />
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{run.total_tests}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-emerald-500">
                    {run.passed}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right font-mono text-sm",
                      run.failed > 0 ? "text-rose-500" : "text-muted-foreground",
                    )}
                  >
                    {run.failed}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">
                    {run.skipped}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right font-mono text-sm",
                      run.xfailed > 0 ? "text-yellow-600" : "text-muted-foreground",
                    )}
                  >
                    {run.xfailed}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      {(job.tests?.length ?? 0) > 0 && (
        <div>
          <h2 className="mb-2 text-sm font-medium text-muted-foreground">
            All tests ({job.tests.length})
          </h2>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Test</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Operation</TableHead>
                  <TableHead>GPU</TableHead>
                  <TableHead>Machine(s)</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead>Run</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {job.tests.map((test) => (
                  <TableRow
                    key={test.result_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/ci/${test.run_id}/tests/${test.result_id}`)}
                  >
                    <TableCell className="font-mono text-sm">
                      {test.test_name}
                      {test.flaky && (
                        <span className="ml-1.5 text-xs text-muted-foreground">flaky</span>
                      )}
                      {test.known_issue_id && (
                        <span className="ml-1.5 text-xs text-yellow-600">
                          {test.known_issue_id}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <CiStatusBadge status={test.status} />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {test.operation}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{test.gpu_type ?? "-"}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {test.machines ?? "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {formatDuration(test.duration_seconds)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {test.run_id}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
